// Project-asset URL resolver — CDN-only since ASK-124 B2 (RULING 32, 2026-08-30).
//
// boot.json's `projectAssets[slot]` carries `assetKey` — a path key relative to the Forge
// Scene CDN root — and that is now the ONLY address the runtime resolves.
//
// Resolver semantics (one branch, deliberately):
//   1. `assetKey` present AND `VITE_SCENE_CDN_URL` set → `${VITE_SCENE_CDN_URL}/${assetKey}`
//      (the key is the full canonical path — no `assets/` segment).
//   2. Else null.
//
// ── WHAT WAS DELETED HERE, AND WHY IT WAS NOT JUST A CLEANUP ────────────────────────────────
// There used to be a branch (2): "else if `assetUrl` is present, resolve it relative to the
// boot's source base". It was DECISION-020's dual-emit transition read, and by 2026-08-30 the
// CDN plane carried zero `assetUrl`-only slots — but deleting it was still not a no-op, because
// a SECOND plane resolved through it: the bundled boot.json that the old
// `runtime/scripts/build-boot-json.mjs` generated was `assetUrl`-only by construction, and in
// that mode CDN_BASE is null so branch (1) could not fire at all.
//
// That plane was ruled LEGACY — it always was: DECISION-014 (2026-05-23) made the runtime a pure
// static SPA fetching Forge-published artifacts, and the bundled boot survived only as an
// un-taken rollback lever. RULING 32 finished that deletion rather than preserving it, so the
// bundled generator is gone too and there is no second plane left to serve.
//
// 📌 The rule that replaced it: ABSENCE IS NEVER A DEFAULT. A build with no
// `VITE_SCENE_CDN_URL` used to silently degrade to the bundled boot; it now REFUSES at
// `vite.config.ts` (proven red). A missing CDN base is a misconfiguration, not a mode.
//
// Still NOT part of this deletion, and deliberately untouched — none of them reads `assetUrl`:
// the `projects/` source tree, the WP plugin's authoring repository
// (`class-kapowx-authoring-repository.php` reads `projects/<id>/project.json` directly), and the
// bundled hub HDRI mirror — `projects/galleria-hub/assets/hdri-daylight-2k.hdr` is the
// last-resort IBL fallback, reached via `resolveEnvAssetUrl` against the runtime ORIGIN.
//
// Future asset-slot consumers (fixture builders, hub signage, decor) call
// `resolveProjectAssetUrl()` rather than reading `.assetKey` directly.

// IMPORTANT: must use the EXACT pattern `import.meta.env.VITE_SCENE_CDN_URL`
// (no optional chaining) so Vite's compile-time static substitution
// triggers. With `?.` chaining Vite skips replacement, the bundle runtime-
// evaluates `import.meta.env` (which is undefined in the browser), and
// CDN_BASE silently degrades to null — defeating the entire dual-path
// preference for assetKey. Discovered the hard way during Phase 1 ship.
const CDN_BASE = (() => {
	try {
		const raw = import.meta.env.VITE_SCENE_CDN_URL;
		if (typeof raw === "string" && raw.length > 0) return raw.replace(/\/$/, "");
	} catch { /* not running under Vite (e.g. CLI test) — fall through */ }
	return null;
})();

// The BOOT OBJECT the bundle reads, relative to the CDN base (coord ASK-177 · preview path). Default `boot.json` — the
// live pointer, so every plane build is unchanged. A PREVIEW build sets `VITE_SCENE_BOOT_OBJECT` to a candidate boot
// (`candidates/<sha>/preview/boot.json`, written by scripts/candidate-preview.mjs) and keeps the CDN base at the bucket
// root, so the content-addressed keys every other shop/catalogue/fixture carries still resolve where forge put them —
// the same split as forge's own ASK-093 candidate mint (BOOT_OBJECT + CANDIDATE_PREFIX; content-addressed keys at root).
// Same env name and default as runtime/src/headless-probe.ts. Exact `import.meta.env.X` pattern for Vite substitution.
const BOOT_OBJECT_RAW = (() => {
	try {
		const raw = import.meta.env.VITE_SCENE_BOOT_OBJECT;
		if (typeof raw === "string" && raw.length > 0) return raw;
	} catch { /* not running under Vite */ }
	return null;
})();

/** PURE: a boot-object key → the key to fetch. Empty → `boot.json`; a key that is absolute, escapes the base or is not
 *  a `.json` object throws (a misbuilt preview must fail loudly, never fall back to the live pointer). */
export function normalizeBootObject(raw) {
	if (raw == null || raw === "") return "boot.json";
	const key = String(raw).replace(/^\/+/, "");
	if (/^[a-z]+:/i.test(key) || key.split("/").some((seg) => seg === ".." || seg === "") || !/\.json$/i.test(key)) {
		throw new Error(`[runtime] VITE_SCENE_BOOT_OBJECT "${raw}" is not a relative .json key under the CDN base`);
	}
	return key;
}

/** PURE: the boot URL a bundle reads. `site:<file>.json` (ASK-178 §6 item 5, a SUBMISSION preview: the preview boot ships
 *  INSIDE the channel's own deploy, so nothing is written to the public bucket — D-089 cl.4) → the same-origin root path
 *  `/<file>.json`; anything else → `${cdn}/${normalizeBootObject(raw)}` (null with no CDN base). The `site:` file obeys the
 *  same rule (a relative .json key, no escape). */
export function bootObjectUrl(cdn, raw) {
	if (typeof raw === "string" && raw.startsWith("site:")) return `/${normalizeBootObject(raw.slice(5))}`;
	return cdn ? `${cdn}/${normalizeBootObject(raw)}` : null;
}

/** The boot URL for this bundle: `${VITE_SCENE_CDN_URL}/${boot object}`; null with no CDN base. */
export function sceneBootUrl() {
	if (!CDN_BASE) return null;
	return bootObjectUrl(CDN_BASE, BOOT_OBJECT_RAW);
}

/**
 * Resolve a project-asset slot to a fetchable URL.
 *
 * @param {Record<string, { assetKey?: string, assetUrl?: string }> | null | undefined} projectAssets
 * @param {string} slot — the slot key in projectAssets (e.g. "shop", "hub", "spinnerRack")
 * @returns {string | null} the resolved URL, or null if the slot is absent / empty
 */
export function resolveProjectAssetUrl(projectAssets, slot) {
	const entry = projectAssets?.[slot];
	if (!entry) return null;
	// An ABSOLUTE assetKey passes through unchanged, as resolveAssetKeyUrl does: a submission
	// preview boot (D-089 cl.4) carries signed staging URLs as keys, and the CDN prefix turned them
	// into `${CDN}/https://…` → 403 on the merged hub + the sky pair (coord ASK-178 §16a/§17).
	if (typeof entry.assetKey === "string" && /^https?:\/\//i.test(entry.assetKey)) return entry.assetKey;
	if (typeof entry.assetKey === "string" && entry.assetKey.length > 0 && CDN_BASE) {
		// assetKey is the COMPLETE canonical path per DECISION-020
		// (`<shopId>/<librarySha>/<file>`) — no `assets/` segment. The old
		// `/assets/` prepend matched the pre-ASK-053 uploader drift; the
		// forge-service fix unified producer+consumer on the clean scheme
		// (verified: bytes 200 at `<shop>/<sha>/…`, 404 at `assets/<shop>/…`).
		return `${CDN_BASE}/${entry.assetKey}`;
	}
	// No `assetUrl` branch: RULING 32 deleted it with the bundled plane it served. A slot that
	// carries only an `assetUrl` is now unresolvable BY DESIGN — a mint that emits one is a defect
	// in the mint, and returning null surfaces it instead of quietly resolving a legacy address.
	return null;
}

/**
 * Resolve a RAW canonical asset key (not a projectAssets slot) → CDN URL. Used for
 * lighting-recipe `environment.asset`/`.assetLod` (relative CDN paths, M7 Chunk 3).
 * Absolute URLs (http..., the hub HDRI fallback) pass through unchanged.
 * @returns {string | null}
 */
export function resolveAssetKeyUrl(key) {
	if (typeof key !== "string" || !key) return null;
	if (/^https?:\/\//i.test(key)) return key;            // already absolute
	if (!CDN_BASE) return null;                            // no CDN configured (bundled dev) → caller skips
	return `${CDN_BASE}/${key.replace(/^\//, "")}`;
}

/**
 * Resolve a SCENE BINDING (`scene.runtimeBindings.sharedComic`, …) → CDN URL, by its `assetKey`
 * ONLY — the same rule as a `projectAssets` slot (RULING 32), extended to the bindings by Client 4ad
 * (ASK-159 §2.18hb 🟣 2 step 1). The binding's `assetUrl` is NEVER read: it was loaded against the
 * runtime ORIGIN, where demo-store's `projects/…` answered from the bundled tree (the second plane
 * RULING 32 retired) and test-shop's key-shaped URL answered the SPA's index.html (200 text/html),
 * so the shared comic silently fell to the procedural template on every non-demo shop.
 * A binding with no `assetKey` → null, and the caller names its fallback.
 * @param {{ assetKey?: string, assetUrl?: string } | null | undefined} binding
 * @returns {string | null}
 */
export function resolveBindingAssetUrl(binding) {
	return resolveAssetKeyUrl(binding?.assetKey);
}

/**
 * Resolve a recipe ENVIRONMENT / HDRI asset against the RUNTIME ORIGIN (the SPA base), NOT the Scene
 * CDN. The curated HDRIs + the hub fallback ship BUNDLED with the runtime (served 200 at the origin);
 * they are NOT published to the CDN (404 → no IBL → dark shop, ASK-099 / D-059 item 1). Absolute /
 * `data:` URLs pass through. `BASE_URL` is the SPA base path ("/" normally); resolving against it
 * keeps working when the boot itself is loaded from the CDN (bundled assets stay origin-served).
 * @returns {string | null}
 */
export function resolveEnvAssetUrl(asset) {
	if (typeof asset !== "string" || !asset) return null;
	if (/^(https?:)?\/\//i.test(asset) || /^data:/i.test(asset)) return asset;   // already absolute
	let base = "/";
	try { const b = import.meta.env.BASE_URL; if (typeof b === "string" && b) base = b; } catch { /* CLI/non-Vite */ }
	return `${base.replace(/\/$/, "")}/${asset.replace(/^\//, "")}`;
}

// ── ASK-124 B3 — TEMPLATE-FAMILY ENV: one copy per pin, not one per stamped shop ──
//
// A lighting recipe's `environment.asset` is a property of the template FAMILY (it lives
// in `boot.templatePresets[<family>]["lighting-recipe"][<preset>].environment`), and its
// path is already written relative to the templates tree — `env/industrial-loft-2k.hdr`.
// Nothing about it is per-shop. But the runtime only ever resolved it against the shop's
// own minted base, so the mint had to COPY the whole HDRI set into every stamped shop's
// package, on every pin advance.
//
// MEASURED on `forge-artifacts-dev`, 2026-08-27 (full bucket walk, 14,472 objects):
//   907 `.hdr` objects · 3.909 GB — 35% of the entire 11.10 GB bucket
//   7 distinct contents by md5 · 30.4 MB
//   → 129x duplication, 3.88 GB of pure copies, across 99 per-shop sha directories
// And it buys nothing: only `demo-store` actually has the copies. `galleria-hub`,
// `test-shop` and `vanessa-gg-comics-a2yp` all 404 on their recipe HDRI today and run the
// bundled hub fallback silently (deferrals row 31 / ASK-124 A4).
//
// The shared key is DERIVED, not declared — `boot.templatesSha` is the pin that already
// identifies the templates tree the recipe itself came from, so no new boot field and no
// new schema. It is the same `<scope>/<sha>/<path>` convention every other artifact uses,
// with the scope being the templates tree instead of a shop.
//
// ✅ THE DELETION HAS LANDED (2026-08-30, ASK-124 B3 / RULING 21 · deferrals row 28).
// The dual path was never meant to be permanent — it existed only while Content's mint was
// still writing per-shop copies, and the trigger in `test-asset-url-resolver.mjs` measured
// the shared key on every run rather than carrying the fact in prose. It printed MET, twice
// over:
//   · the shared key serves 6/6 recipe env assets on the live pin (my measurement)
//   · it serves 4/4 shops, and every per-shop copy now 404s (Coord's, 2026-08-30)
// So `resolveShopAssetUrl` is GONE — it had no caller but this one — and the per-shop leg
// with it. What remains is a one-element candidate list, kept as a list because the loader's
// contract is "try these in order" and because the shape is what let the migration land
// without a flag day: the shared key went in FRONT of the per-shop one, served, and the dead
// leg was then removed with nothing depending on it.
//
// The trigger did not become a comment when it fired — it became an ASSERTION. With no
// fallback left, "the shared key serves every recipe env asset" is now load-bearing for
// every shop's IBL, so the test reds if it stops being true instead of quietly reporting it.

/**
 * Resolve a template-family lighting-recipe env asset against the SHARED per-pin base
 * `templates/${templatesSha}/`. One copy per pin, shared by every shop stamped from any
 * family at that pin. Absolute URLs pass through. Returns null with no CDN or no pin
 * (bundled dev) so the caller falls through to its next candidate.
 * @param {string} templatesSha — `boot.templatesSha`, the pin the recipe came from
 * @param {string} asset — the recipe's relative path, e.g. "env/industrial-loft-2k.hdr"
 * @returns {string | null}
 */
export function resolveTemplateEnvAssetUrl(templatesSha, asset) {
	if (typeof asset !== "string" || !asset) return null;
	if (/^(https?:)?\/\//i.test(asset) || /^data:/i.test(asset)) return asset;
	if (!CDN_BASE || !templatesSha) return null;
	return `${CDN_BASE}/templates/${templatesSha}/${asset.replace(/^\//, "")}`;
}

/**
 * The ORDERED candidate list for one recipe env asset — the shared per-pin key, and
 * nothing else since B3 retired the per-shop leg. Nulls are dropped so the caller never
 * carries a hole, and duplicates are dropped so it never issues the same GET twice (the
 * loader treats every candidate as a real GET; a repeated URL is a wasted round trip, and
 * an aborted duplicate is what tripped the probe asset gate under ASK-099 item 1).
 *
 * Still a LIST, deliberately, with one element today: the loader's contract is "try these
 * in order", and keeping the shape is what makes the next env-key migration a re-order
 * rather than a rewrite — which is exactly how this one landed without a flag day.
 *
 * The bundled hub HDRI is deliberately NOT in this list: it is the last-resort fallback
 * and it resolves against the runtime ORIGIN, not the CDN — a different resolver and a
 * different failure meaning ("this shop has no curated HDRI" vs "this pin has no shared
 * copy"). The caller keeps them distinct.
 *
 * @returns {string[]} zero or one URL (a list by contract, not by count)
 */
export function envAssetCandidates({ templatesSha, asset }) {
	const out = [];
	for (const u of [resolveTemplateEnvAssetUrl(templatesSha, asset)]) {
		if (u && !out.includes(u)) out.push(u);
	}
	return out;
}

// Diagnostic helper for stage.js's first-paint log. Returns "(CDN)" /
// "(legacy)" / "(missing)" so the console line tells operators which
// path served each scene's shell.
export function describeAssetSource(projectAssets, slot) {
	const entry = projectAssets?.[slot];
	if (!entry) return "(missing)";
	if (typeof entry.assetKey === "string" && entry.assetKey.length > 0 && CDN_BASE) return "(Forge CDN — assetKey)";
	if (typeof entry.assetUrl === "string" && entry.assetUrl.length > 0) return "(legacy — assetUrl)";
	return "(missing)";
}
